import {useContext, useState} from 'react'

import {htmlStringToReactElement} from '@s-ui/js/lib/react'
import AtomBadge, {atomBadgeDesigns, atomBadgeSizes, atomBadgeTypes} from '@s-ui/react-atom-badge'
import AtomButton, {
  atomButtonColors,
  atomButtonDesigns,
  atomButtonShapes,
  atomButtonSizes
} from '@s-ui/react-atom-button'
import AtomIcon, {ATOM_ICON_SIZES, atomIconSizes} from '@s-ui/react-atom-icon'
import AtomImage from '@s-ui/react-atom-image'
import Context from '@s-ui/react-context'
import useMount from '@s-ui/react-hooks/lib/useMount'
import {
  MoleculeDrawer,
  MoleculeDrawerOverlay,
  moleculeDrawerPlacements,
  moleculeDrawerSizes
} from '@s-ui/react-molecule-drawer'
import {useLocation} from '@s-ui/react-router'
import BadgeDealerRating from '@adv-mt/mt-rating-badge'
import IconCheck from '@adv-mt/mt-svg-icons/lib/CircleCheck.js'
import IconClose from '@adv-mt/mt-svg-icons/lib/Close'
import IconInfo from '@adv-mt/mt-svg-icons/lib/InfoOutline.js'
import TitleBasic, {titleBasicSizes} from '@adv-mt/mt-title-basic'

import {useMessagingContext} from '../../useMessagingContext/index.js'

export const ReservationPaymentSuccessDrawer = () => {
  const {state: routerState} = useLocation()
  const {i18n, domain} = useContext(Context)
  const {setInteractionComponent} = useMessagingContext()

  const [adDetails, setAdsDetails] = useState({})
  const adsDetailsAvailable = Object.keys(adDetails).length > 0

  const config = domain.get('config')
  const urlStatics = config.get('urlStatics')
  const emptyVehicleImage = config.get('technicalDetails').TECHNICAL_DETAILS_EMPTY_VEHICLE_IMAGE
  const defaultImage = `${urlStatics}${emptyVehicleImage}`

  useMount(() => {
    setAdsDetails(routerState?.adDetails || {})
  })

  const handleClose = () => {
    setInteractionComponent({component: null, show: false})
  }

  const handleGoToChat = () => {
    setInteractionComponent({component: null, show: false})
  }

  return (
    <MoleculeDrawerOverlay isVisible>
      <MoleculeDrawer
        closeOnOutsideClick
        isOpen
        onClose={handleClose}
        placement={moleculeDrawerPlacements.RIGHT}
        size={moleculeDrawerSizes.AUTO}
      >
        <div className="mt-ReservationPaymentSuccessDrawer-wrapper">
          <div className="mt-ReservationPaymentSuccessDrawer-header">
            <div className="mt-ReservationPaymentSuccessDrawer-closeButton">
              <AtomButton
                design={atomButtonDesigns.FLAT}
                leftIcon={
                  <AtomIcon as="button" size={ATOM_ICON_SIZES.medium}>
                    <IconClose />
                  </AtomIcon>
                }
                onClick={handleClose}
                shape={atomButtonShapes.CIRCULAR}
              />
            </div>
            <TitleBasic size={titleBasicSizes.s} title={i18n.t('VEHICLE_RESERVATION.DRAWER_PAYMENT_SUCCESS.TITLE')} />
          </div>
          <div className="mt-ReservationPaymentSuccessDrawer-body">
            {adsDetailsAvailable ? (
              <>
                <span className="mt-ReservationPaymentSuccessDrawer-adTitle">{adDetails?.ad?.title}</span>
                <div className="mt-ReservationPaymentSuccessDrawer-imageWrapper">
                  <AtomBadge
                    className="mt-ReservationPaymentSuccessDrawer-badgeReservationSuccess"
                    design={atomBadgeDesigns.SOFT}
                    icon={<IconCheck color="currentColor" size={atomIconSizes.small} />}
                    label={i18n.t('VEHICLE_RESERVATION.DRAWER_PAYMENT_SUCCESS.BADGE')}
                    size={atomBadgeSizes.LARGE}
                    type={atomBadgeTypes.SUCCESS}
                  />
                  {adDetails?.ad?.price && (
                    <AtomBadge
                      className="mt-ReservationPaymentSuccessDrawer-badgePrice"
                      size={atomBadgeSizes.LARGE}
                      type={atomBadgeTypes.NEUTRAL}
                      label={i18n.c(adDetails.ad.price)}
                    />
                  )}
                  {adDetails?.financingData?.fee && (
                    <AtomBadge
                      className="mt-ReservationPaymentSuccessDrawer-badgeInstalment"
                      size={atomBadgeSizes.LARGE}
                      type={atomBadgeTypes.NEUTRAL}
                      label={`${i18n.c(adDetails.financingData.fee)}/mes`}
                    />
                  )}
                  <AtomImage
                    src={adDetails?.ad?.photos?.[0] || defaultImage}
                    className="mt-ReservationPaymentSuccessDrawer-image"
                    role="presentation"
                  />
                </div>
                {adDetails?.seller?.dealer?.ratings && (
                  <div className="mt-ReservationPaymentSuccessDrawer-dealerRatingBadge">
                    <BadgeDealerRating value={adDetails.seller.dealer.ratings.average} />
                  </div>
                )}
                <span className="mt-ReservationPaymentSuccessDrawer-dealerName">
                  {adDetails?.seller?.dealer?.name?.toUpperCase()}
                </span>
                <div className="mt-ReservationPaymentSuccessDrawer-dealerAddress">
                  {adDetails?.seller?.dealer?.location?.address}, {adDetails?.seller?.dealer?.location?.postalCode}{' '}
                  {adDetails?.seller?.dealer?.location?.city}
                </div>
                <div className="mt-ReservationPaymentSuccessDrawer-helpText">
                  <IconInfo color="currentColor" size={atomIconSizes.medium} />
                  <span className="mt-ReservationPaymentSuccessDrawer-helpTextContent">
                    {htmlStringToReactElement(i18n.t('VEHICLE_RESERVATION.DRAWER_PAYMENT_SUCCESS.HELP_TEXT'))}
                  </span>
                </div>
              </>
            ) : (
              <div className="mt-ReservationPaymentSuccessDrawer-helpText">
                <IconInfo color="currentColor" size={atomIconSizes.medium} />
                <span className="mt-ReservationPaymentSuccessDrawer-helpTextContent">
                  {htmlStringToReactElement(i18n.t('VEHICLE_RESERVATION.DRAWER_PAYMENT_SUCCESS.HELP_TEXT'))}
                </span>
              </div>
            )}
          </div>
          <div className="mt-ReservationPaymentSuccessDrawer-footer">
            <AtomButton
              color={atomButtonColors.ACCENT}
              onClick={handleGoToChat}
              shape={atomButtonShapes.CIRCULAR}
              size={atomButtonSizes.LARGE}
              fullWidth
            >
              {i18n.t('VEHICLE_RESERVATION.DRAWER_PAYMENT_SUCCESS.CTA')}
            </AtomButton>
          </div>
        </div>
      </MoleculeDrawer>
    </MoleculeDrawerOverlay>
  )
}
